import { downloadTicketAttachment } from "@/actions/form";
import { TicketAttachment } from "@/types/action";
import { AttachFile } from "@mui/icons-material";
import { Box, Button, Chip, Typography } from "@mui/material";
import { ChangeEvent, FC, useRef } from "react";

// Keep in sync with TICKET_ATTACHMENT_MAX_SIZE in the backend settings
export const MAX_TICKET_ATTACHMENT_SIZE = 10 * 1024 * 1024;

export const formatFileSize = (bytes: number) => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Returns an error message for the first invalid file, or null if all are valid
export const validateTicketFiles = (files: File[]): string | null => {
	for (const file of files) {
		if (file.size === 0) return `${file.name} is empty.`;
		if (file.size > MAX_TICKET_ATTACHMENT_SIZE) {
			return `${file.name} is larger than the ${formatFileSize(MAX_TICKET_ATTACHMENT_SIZE)} limit.`;
		}
	}
	return null;
};

interface TicketFilePickerProps {
	files: File[];
	onChange: (files: File[]) => void;
	multiple?: boolean;
	disabled?: boolean;
}

export const TicketFilePicker: FC<TicketFilePickerProps> = ({
	files,
	onChange,
	multiple = false,
	disabled = false,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
		const selected = Array.from(e.target.files ?? []);
		onChange(multiple ? [...files, ...selected] : selected.slice(0, 1));
		// Allow selecting the same file again after removing it
		e.target.value = "";
	};

	return (
		<Box>
			<Button
				variant="outlined"
				size="small"
				startIcon={<AttachFile />}
				disabled={disabled}
				onClick={() => inputRef.current?.click()}
			>
				{multiple ? "Attach Files" : "Attach File"}
			</Button>
			<input
				ref={inputRef}
				type="file"
				hidden
				multiple={multiple}
				onChange={handleSelect}
			/>
			<Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
				Max {formatFileSize(MAX_TICKET_ATTACHMENT_SIZE)} per file
			</Typography>
			{files.length > 0 && (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
					{files.map((file, index) => (
						<Chip
							key={`${file.name}-${index}`}
							label={`${file.name} (${formatFileSize(file.size)})`}
							color={file.size > MAX_TICKET_ATTACHMENT_SIZE ? "error" : "default"}
							onDelete={
								disabled
									? undefined
									: () => onChange(files.filter((_, i) => i !== index))
							}
							size="small"
						/>
					))}
				</Box>
			)}
		</Box>
	);
};

export const TicketAttachmentList: FC<{ attachments?: TicketAttachment[] }> = ({
	attachments,
}) => {
	if (!attachments?.length) return null;
	return (
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
			{attachments.map((attachment) => (
				<Chip
					key={attachment.id}
					icon={<AttachFile />}
					label={`${attachment.file_name} (${formatFileSize(attachment.size)})`}
					onClick={() => downloadTicketAttachment(attachment)}
					variant="outlined"
					size="small"
				/>
			))}
		</Box>
	);
};